import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  ShieldAlert, Users, Activity, LogIn, MonitorCheck, ScrollText, Clock,
  Search, RefreshCw, Download, FileSpreadsheet, Loader2, XCircle, Eye,
  UserCheck, LogOut, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const DATE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "Last 7 Days" },
  { key: "last30", label: "Last 30 Days" },
  { key: "custom", label: "Custom Range" },
];

const EVENT_STYLE = {
  login: "bg-emerald-500/20 text-emerald-700 border-emerald-500/40",
  logout: "bg-slate-500/20 text-slate-700 border-slate-500/40",
  failed_login: "bg-red-500/20 text-red-700 border-red-500/40",
  screen_opened: "bg-sky-500/20 text-sky-700 border-sky-500/40",
  report_downloaded: "bg-amber-500/20 text-amber-700 border-amber-500/40",
  pdf_downloaded: "bg-amber-500/20 text-amber-700 border-amber-500/40",
  excel_downloaded: "bg-amber-500/20 text-amber-700 border-amber-500/40",
  file_uploaded: "bg-indigo-500/20 text-indigo-700 border-indigo-500/40",
  record_created: "bg-teal-500/20 text-teal-700 border-teal-500/40",
  record_updated: "bg-blue-500/20 text-blue-700 border-blue-500/40",
  record_deleted: "bg-rose-500/20 text-rose-700 border-rose-500/40",
  approval_completed: "bg-purple-500/20 text-purple-700 border-purple-500/40",
  data_frozen: "bg-cyan-500/20 text-cyan-700 border-cyan-500/40",
  data_unfrozen: "bg-cyan-500/20 text-cyan-700 border-cyan-500/40",
  custom: "bg-slate-500/20 text-slate-700 border-slate-500/40",
};

const rangeFor = (key) => {
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date();
  let start = new Date();
  if (key === "today") start = startToday;
  else if (key === "yesterday") {
    start = new Date(startToday);
    start.setDate(start.getDate() - 1);
    end.setTime(startToday.getTime() - 1);
  } else if (key === "last7") {
    start = new Date(now);
    start.setDate(start.getDate() - 7);
  } else if (key === "last30") {
    start = new Date(now);
    start.setDate(start.getDate() - 30);
  }
  return { from: start.toISOString(), to: end.toISOString() };
};

const fmtDT = (iso) => {
  if (!iso) return "—";
  try { const d = new Date(iso); return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`; }
  catch { return iso; }
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};
const fmtDur = (s) => {
  if (!s || s <= 0) return "—";
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};
const eventLabel = (t) => (t || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function Tile({ icon: Icon, label, value, tone = "sky", testid }) {
  const toneMap = {
    sky:      "from-sky-50 to-sky-100 border-sky-200 text-sky-900",
    emerald:  "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900",
    amber:    "from-amber-50 to-amber-100 border-amber-200 text-amber-900",
    rose:     "from-rose-50 to-rose-100 border-rose-200 text-rose-900",
    purple:   "from-purple-50 to-purple-100 border-purple-200 text-purple-900",
    teal:     "from-teal-50 to-teal-100 border-teal-200 text-teal-900",
  };
  return (
    <div className={`rounded-xl border-2 bg-gradient-to-br p-3 shadow-sm ${toneMap[tone] || toneMap.sky}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="w-4 h-4" />}
        <p className="text-[11px] uppercase tracking-wider font-bold">{label}</p>
      </div>
      <p className="text-2xl font-extrabold">{value}</p>
    </div>
  );
}

export default function ActivityLog() {
  const { session } = useAuth();
  const token = session?.token;
  const isAllowed = Boolean(session && (session.is_super_admin || session.is_admin));

  // Filter state
  const [preset, setPreset] = useState("today");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("all");
  const [center, setCenter] = useState("all");
  const [screen, setScreen] = useState("");
  const [loginStatus, setLoginStatus] = useState("all");

  // Data state
  const [tiles, setTiles] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [centers, setCenters] = useState([]);
  const [openSession, setOpenSession] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const range = useMemo(() => {
    if (preset === "custom" && dateFrom && dateTo) {
      return {
        from: new Date(dateFrom).toISOString(),
        to: new Date(new Date(dateTo).getTime() + 86400000 - 1).toISOString(),
      };
    }
    return rangeFor(preset);
  }, [preset, dateFrom, dateTo]);

  // Fetch centers for the filter dropdown
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/centers`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }).then((r) => r.ok ? r.json() : { centers: [] })
      .then((d) => setCenters(d.centers || d || []))
      .catch(() => {});
  }, [token]);

  const loadAll = useCallback(async () => {
    if (!token || !isAllowed) return;
    setLoading(true);
    try {
      const [sumRes, listRes] = await Promise.all([
        fetch(`${API}/api/activity-log/summary`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, date_from: range.from, date_to: range.to }),
        }),
        fetch(`${API}/api/activity-log/list`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token,
            date_from: range.from, date_to: range.to,
            search: search || null,
            role: role === "all" ? null : role,
            center: center === "all" ? null : center,
            screen: screen || null,
            login_status: loginStatus === "all" ? null : loginStatus,
            limit: 500,
          }),
        }),
      ]);
      if (sumRes.ok) setTiles((await sumRes.json()).tiles);
      if (listRes.ok) setSessions((await listRes.json()).sessions || []);
    } catch (e) {
      toast.error("Failed to load activity log");
    } finally { setLoading(false); }
  }, [token, isAllowed, range.from, range.to, search, role, center, screen, loginStatus]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openTimeline = async (row) => {
    setOpenSession(row);
    setTimeline([]);
    setTimelineLoading(true);
    try {
      const res = await fetch(`${API}/api/activity-log/user-timeline`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, session_id: row.session_id, limit: 1000 }),
      });
      const data = await res.json();
      setTimeline(data.events || []);
    } catch { toast.error("Failed to load timeline"); }
    finally { setTimelineLoading(false); }
  };

  // ── CSV / Excel export ─────────────────────────────────────
  const downloadExcel = async () => {
    if (!sessions.length) { toast.info("Nothing to export"); return; }
    try {
      const XLSX = await import("xlsx");
      const wb = XLSX.utils.book_new();
      const rows = sessions.map((s) => ({
        user_name: s.actor_name,
        uid_or_email: s.actor_email || s.actor_uid,
        role: s.role,
        center: s.center,
        login_at: s.login_at,
        last_activity_at: s.last_activity_at,
        logout_at: s.logout_at || "",
        session_duration_secs: s.session_duration_secs,
        screens_opened: s.screens_opened_count,
        status: s.logout_at ? "Logged Out" : "Active",
        ip: s.ip,
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Sessions");
      XLSX.writeFile(wb, `ActivityLog_${new Date().toISOString().split("T")[0]}.xlsx`);
      toast.success(`Exported ${rows.length} sessions`);
    } catch (e) { toast.error(e.message || "Excel export failed"); }
  };

  const downloadPDF = async () => {
    if (!sessions.length) { toast.info("Nothing to export"); return; }
    try {
      const { jsPDF } = await import("jspdf");
      const autoTableMod = await import("jspdf-autotable").catch(() => null);
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      doc.setFontSize(16); doc.setTextColor(139, 0, 0);
      doc.text("Purnabramha — Login & User Activity Log", 40, 40);
      doc.setFontSize(10); doc.setTextColor(60);
      doc.text(`Range: ${new Date(range.from).toLocaleString()} → ${new Date(range.to).toLocaleString()}`, 40, 58);
      doc.text(`Generated: ${new Date().toLocaleString()} by ${session.managerName || session.mobile}`, 40, 72);

      const head = [["User", "UID / Email", "Role", "Center", "Login", "Last Activity", "Logout", "Duration", "Screens", "Status"]];
      const body = sessions.map((s) => [
        s.actor_name || "—",
        s.actor_email || s.actor_uid || "—",
        s.role || "—",
        s.center || "—",
        fmtDT(s.login_at),
        fmtTime(s.last_activity_at),
        s.logout_at ? fmtTime(s.logout_at) : "Active",
        fmtDur(s.session_duration_secs),
        s.screens_opened_count || 0,
        s.logout_at ? "Logged Out" : "Active",
      ]);

      if (autoTableMod && typeof doc.autoTable === "function") {
        doc.autoTable({
          head, body, startY: 90, styles: { fontSize: 8 },
          headStyles: { fillColor: [139, 0, 0] },
        });
      } else {
        // Fallback simple table
        let y = 100;
        doc.setFontSize(9);
        head[0].forEach((h, i) => doc.text(h, 40 + i * 75, y));
        y += 12;
        body.forEach((r) => {
          r.forEach((c, i) => doc.text(String(c).slice(0, 15), 40 + i * 75, y));
          y += 12;
          if (y > 550) { doc.addPage(); y = 40; }
        });
      }
      doc.save(`ActivityLog_${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(e.message || "PDF export failed"); }
  };

  // ── Access guard ───────────────────────────────────────────
  if (!isAllowed) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Card className="border-red-200">
          <CardContent className="p-8 text-center space-y-3">
            <ShieldAlert className="w-10 h-10 mx-auto text-red-600" />
            <h2 className="text-lg font-bold text-red-800">Management Access Required</h2>
            <p className="text-sm text-slate-600">
              This screen is restricted to authorised Management, Founder and Super Admin roles.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4 max-w-[1600px] mx-auto" data-testid="activity-log-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#8B0000]" /> Login & User Activity Log
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Audit trail of user logins, screens opened, downloads, uploads and record changes
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadAll} disabled={loading} data-testid="al-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Refresh
          </Button>
          <Button variant="outline" onClick={downloadExcel} data-testid="al-export-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button variant="outline" onClick={downloadPDF} data-testid="al-export-pdf-btn">
            <Download className="w-4 h-4 mr-1" /> PDF
          </Button>
        </div>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="al-tiles">
        <Tile icon={UserCheck} label="Users Logged In Today" value={tiles?.users_logged_in_today ?? "—"} tone="emerald" testid="al-tile-users-today" />
        <Tile icon={Activity} label="Active Users Now" value={tiles?.active_users_now ?? "—"} tone="teal" testid="al-tile-active-now" />
        <Tile icon={LogIn} label="Total Logins Today" value={tiles?.total_logins_today ?? "—"} tone="sky" testid="al-tile-logins-today" />
        <Tile icon={MonitorCheck} label="Screens Opened Today" value={tiles?.screens_opened_today ?? "—"} tone="purple" testid="al-tile-screens-today" />
        <Tile icon={XCircle} label="Failed Login Attempts" value={tiles?.failed_login_attempts_today ?? "—"} tone="rose" testid="al-tile-failed-today" />
        <Tile icon={Users} label="Active Users (7 days)" value={tiles?.active_users_last_7_days ?? "—"} tone="amber" testid="al-tile-active7" />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs">Date Range</Label>
              <Select value={preset} onValueChange={setPreset}>
                <SelectTrigger className="h-9" data-testid="al-preset-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DATE_PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {preset === "custom" && (
              <>
                <div>
                  <Label className="text-xs">From</Label>
                  <Input type="date" className="h-9" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="al-date-from" />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input type="date" className="h-9" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="al-date-to" />
                </div>
              </>
            )}
            <div className={preset === "custom" ? "" : "md:col-span-2"}>
              <Label className="text-xs">Search (email, uid, name, center)</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                <Input className="h-9 pl-8" value={search} onChange={(e) => setSearch(e.target.value)}
                       placeholder="e.g. rakesh@ / 97413..." data-testid="al-search" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="h-9" data-testid="al-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="franchise_owner">Franchise Owner</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Center</Label>
              <Select value={center} onValueChange={setCenter}>
                <SelectTrigger className="h-9" data-testid="al-center-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Centers</SelectItem>
                  {centers.map((c) => (
                    <SelectItem key={c.code || c} value={c.code || c}>{c.code || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Login Status</Label>
              <Select value={loginStatus} onValueChange={setLoginStatus}>
                <SelectTrigger className="h-9" data-testid="al-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="logged_out">Logged Out</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sessions Table */}
      <Card>
        <CardContent className="p-0">
          <div className="p-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <ScrollText className="w-4 h-4" /> Sessions
              <Badge variant="outline">{sessions.length}</Badge>
            </div>
          </div>
          {loading ? (
            <div className="py-8 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : sessions.length === 0 ? (
            <div className="py-10 text-center text-slate-500">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No sessions found for the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>UID / Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Center</TableHead>
                    <TableHead>Login</TableHead>
                    <TableHead>Last Activity</TableHead>
                    <TableHead>Logout</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Screens</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((s) => (
                    <TableRow key={s.session_id} data-testid={`al-row-${s.session_id.slice(0, 8)}`}>
                      <TableCell className="font-medium">{s.actor_name || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{s.actor_email || s.actor_uid || "—"}</TableCell>
                      <TableCell className="text-xs">{s.role || "—"}</TableCell>
                      <TableCell>{s.center || "—"}</TableCell>
                      <TableCell className="text-xs">{fmtDT(s.login_at)}</TableCell>
                      <TableCell className="text-xs">{fmtTime(s.last_activity_at)}</TableCell>
                      <TableCell className="text-xs">{s.logout_at ? fmtTime(s.logout_at) : "—"}</TableCell>
                      <TableCell className="text-xs">{fmtDur(s.session_duration_secs)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{s.screens_opened_count || 0}</Badge>
                      </TableCell>
                      <TableCell>
                        {s.logout_at ? (
                          <Badge className="bg-slate-500/20 text-slate-700 border-slate-500/40">
                            <LogOut className="w-3 h-3 mr-1" /> Logged Out
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/40">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => openTimeline(s)}
                          data-testid={`al-view-${s.session_id.slice(0, 8)}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Timeline Dialog */}
      <Dialog open={!!openSession} onOpenChange={(o) => { if (!o) { setOpenSession(null); setTimeline([]); } }}>
        <DialogContent className="max-w-3xl" data-testid="al-timeline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#8B0000]" />
              Activity Timeline — {openSession?.actor_name} · {openSession?.center}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1 text-sm">
            <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 pb-2 border-b">
              <div><b>UID:</b> {openSession?.actor_email || openSession?.actor_uid}</div>
              <div><b>Role:</b> {openSession?.role}</div>
              <div><b>Login:</b> {fmtDT(openSession?.login_at)}</div>
              <div><b>Duration:</b> {fmtDur(openSession?.session_duration_secs)}</div>
            </div>
            {timelineLoading ? (
              <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>
            ) : timeline.length === 0 ? (
              <div className="py-6 text-center text-slate-500 text-sm">No activity recorded yet in this session.</div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto pr-1">
                {timeline.map((e) => (
                  <div key={e.event_id} className="flex items-start gap-3 py-1.5 border-b border-slate-100"
                       data-testid={`al-event-${e.event_type}`}>
                    <span className="text-xs font-mono text-slate-500 shrink-0 pt-1 w-20">{fmtTime(e.timestamp)}</span>
                    <Badge className={`shrink-0 text-[10px] ${EVENT_STYLE[e.event_type] || EVENT_STYLE.custom}`}>
                      {eventLabel(e.event_type)}
                    </Badge>
                    <div className="text-xs text-slate-700 flex-1 min-w-0">
                      {e.screen && <div className="font-medium">{e.screen}</div>}
                      {e.details && Object.keys(e.details).length > 0 && (
                        <div className="text-[10px] text-slate-500 truncate" title={JSON.stringify(e.details)}>
                          {JSON.stringify(e.details).slice(0, 120)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
